import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import { SrxApi } from '../api/SrxApi';
import { useAtom } from 'jotai';
import { SrxFsApi } from '../api/SrxFsApi';
import CleanupService from '../utility-services/cleanupService';
import { postNavigationActivity } from '../utility-services/navigationTrackingService';
import {
    highCostAtom, highCostAtomFilteredCount, highCostAtomFiltered, highCostAtomShownAlerts,
    highCostAlertsAtom, highCostAlertsAtomFilteredCount, highCostAlertsAtomFiltered, highCostAlertsAtomShownAlerts,
    clarificationsAtom, clarificationsAtomFilteredCount, clarificationsAtomFiltered, clarificationsAtomShownAlerts,
    priorAuthAtom, priorAuthAtomFilteredCount, priorAuthAtomFiltered, priorAuthAtomShownAlerts,
    nonCoveredAtom, nonCoveredAtomFilteredCount, nonCoveredAtomFiltered, nonCoveredAtomShownAlerts,
    therapeuticIntAtom, therapeuticIntAtomFilteredCount, therapeuticIntAtomFiltered, therapeuticIntAtomShownAlerts,
    admissionMedsAtom, admissionMedsAtomFilteredCount, admissionMedsAtomFiltered, admissionMedsAtomShownAlerts,
    highCostAtomUserSettings,
    highCostAlertsAtomUserSettings,
    clarificationsAtomUserSettings,
    priorAuthAtomUserSettings,
    nonCoveredAtomUserSettings,
    therapeuticIntAtomUserSettings,
    admissionMedsAtomUserSettings,
    refillTooSoonAtomUserSettings,
    pumpPickupAtomUserSettings,
    narcoticsRefillAtomUserSettings,
    alfNewScriptAtomUserSettings,
    alfCostResponseAtomUserSettings,
    unavailableMedicationAlertsAtomUserSettings
} from '../jotai-atoms/AlertAtoms';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import * as Sentry from '@sentry/react';

export const UserContext = createContext();

// export default function useUser(){
//     return useContext(UserContext)
// }

export const UserProvider = (props) => {
    const [user, setUser] = useState(null);
    const [faciToken, setFaciToken] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    
    // AbortController to cancel ongoing requests on cleanup
    const abortControllerRef = React.useRef(null);
    
    // Suppress React's unmounted component warning (false positive - UserProvider never unmounts)
    React.useEffect(() => {
        const originalError = console.error;
        console.error = (...args) => {
            if (typeof args[0] === 'string' && args[0].includes('Can\'t perform a React state update on an unmounted component')) {
                // Suppress this specific warning
                return;
            }
            originalError.apply(console, args);
        };
        
        return () => {
            console.error = originalError;
        };
    }, []);

    let history = useHistory();
    let location = useLocation();

    useEffect(() => {
        if (!user) {
            Sentry.setUser(null);
            return;
        }

        Sentry.setUser({
            id: String(user.userID || user.userId || user.id || user.email || user.username || ''),
            email: user.email,
            username: user.username || user.userName || user.name,
        });
    }, [user]);

    // THIS IS ADDED IN ORDER TO RESET ALL JOTAI ATOMS TO THEIR DEFAULT STATE ON LOGOUT AND NOT TO PRESERVE THE ALERTS

    const resetHighCostAtom = useResetAtom(highCostAtom);
    const resetHighCostAlertsAtom = useResetAtom(highCostAlertsAtom);
    const resetClarificationsAtom = useResetAtom(clarificationsAtom);
    const resetPriorAuthAtom = useResetAtom(priorAuthAtom);
    const resetNonCoveredAtom = useResetAtom(nonCoveredAtom);
    const resetTherapeuticIntAtom = useResetAtom(therapeuticIntAtom);
    const resetAdmissionMedsAtom = useResetAtom(admissionMedsAtom);

    const resetHighCostAtomFilteredCount = useResetAtom(highCostAtomFilteredCount);
    const resetHighCostAlertsAtomFilteredCount = useResetAtom(highCostAlertsAtomFilteredCount);
    const resetClarificationsAtomFilteredCount = useResetAtom(clarificationsAtomFilteredCount);
    const resetPriorAuthAtomFilteredCount = useResetAtom(priorAuthAtomFilteredCount);
    const resetNonCoveredAtomFilteredCount = useResetAtom(nonCoveredAtomFilteredCount);
    const resetTherapeuticIntAtomFilteredCount = useResetAtom(therapeuticIntAtomFilteredCount);
    const resetAdmissionMedsAtomFilteredCount = useResetAtom(admissionMedsAtomFilteredCount);

    const resetHighCostAtomFiltered = useResetAtom(highCostAtomFiltered);
    const resetHighCostAlertsAtomFiltered = useResetAtom(highCostAlertsAtomFiltered);
    const resetClarificationsAtomFiltered = useResetAtom(clarificationsAtomFiltered);
    const resetPriorAuthAtomFiltered = useResetAtom(priorAuthAtomFiltered);
    const resetNonCoveredAtomFiltered = useResetAtom(nonCoveredAtomFiltered);
    const resetTherapeuticIntAtomFiltered = useResetAtom(therapeuticIntAtomFiltered);
    const resetAdmissionMedsAtomFiltered = useResetAtom(admissionMedsAtomFiltered);

    const resetHighCostAtomShownAlerts = useResetAtom(highCostAtomShownAlerts);
    const resetHighCostAlertsAtomShownAlerts = useResetAtom(highCostAlertsAtomShownAlerts);
    const resetClarificationsAtomShownAlerts = useResetAtom(clarificationsAtomShownAlerts);
    const resetPriorAuthAtomShownAlerts = useResetAtom(priorAuthAtomShownAlerts);
    const resetNonCoveredAtomShownAlerts = useResetAtom(nonCoveredAtomShownAlerts);
    const resetTherapeuticIntAtomShownAlerts = useResetAtom(therapeuticIntAtomShownAlerts);
    const resetAdmissionMedsAtomShownAlerts = useResetAtom(admissionMedsAtomShownAlerts);

    // reset alert sort order
    const resetHighCostAtomUserSettings = useResetAtom(highCostAtomUserSettings);
    const resetHighCostAlertsAtomUserSettings = useResetAtom(highCostAlertsAtomUserSettings);
    const resetClarificationsAtomUserSettings = useResetAtom(clarificationsAtomUserSettings);
    const resetPriorAuthAtomUserSettings = useResetAtom(priorAuthAtomUserSettings);
    const resetNonCoveredAtomUserSettings = useResetAtom(nonCoveredAtomUserSettings);
    const resetTherapeuticIntAtomUserSettings = useResetAtom(therapeuticIntAtomUserSettings);
    const resetAdmissionMedsAtomUserSettings = useResetAtom(admissionMedsAtomUserSettings);

    // Hydrate each widget's hide/show + sort-order user setting from the logged-in user's
    // persisted settings (user.userSettings), since nothing else in the app does this and
    // these atoms otherwise just sit at their hardcoded defaults for the whole session.
    const [, setHighCostUserSettings] = useAtom(highCostAtomUserSettings);
    const [, setHighCostAlertsUserSettings] = useAtom(highCostAlertsAtomUserSettings);
    const [, setClarificationsUserSettings] = useAtom(clarificationsAtomUserSettings);
    const [, setPriorAuthUserSettings] = useAtom(priorAuthAtomUserSettings);
    const [, setNonCoveredUserSettings] = useAtom(nonCoveredAtomUserSettings);
    const [, setTherapeuticIntUserSettings] = useAtom(therapeuticIntAtomUserSettings);
    const [, setAdmissionMedsUserSettings] = useAtom(admissionMedsAtomUserSettings);
    const [, setRefillTooSoonUserSettings] = useAtom(refillTooSoonAtomUserSettings);
    const [, setPumpPickupUserSettings] = useAtom(pumpPickupAtomUserSettings);
    const [, setNarcoticsRefillUserSettings] = useAtom(narcoticsRefillAtomUserSettings);
    const [, setAlfNewScriptUserSettings] = useAtom(alfNewScriptAtomUserSettings);
    const [, setAlfCostResponseUserSettings] = useAtom(alfCostResponseAtomUserSettings);
    const [, setUnavailableMedicationAlertsUserSettings] = useAtom(unavailableMedicationAlertsAtomUserSettings);

    useEffect(() => {
        if (!user?.userSettings) return;
        const settings = user.userSettings;

        const hydrateWidgetSettings = (widgetKey, setter) => {
            const showRaw = settings[`widget_${widgetKey}_Show`];
            const sortRaw = settings[`widget_${widgetKey}_SortOrder`];
            if (showRaw === undefined && sortRaw === undefined) return;
            setter((prev) => ({
                sortOrder: sortRaw !== undefined ? Number(sortRaw) : prev.sortOrder,
                hidden: showRaw !== undefined ? !(showRaw === 'true' || showRaw === true) : prev.hidden,
            }));
        };

        hydrateWidgetSettings('highCost', setHighCostUserSettings);
        hydrateWidgetSettings('highCostAlerts', setHighCostAlertsUserSettings);
        hydrateWidgetSettings('openBatch', setClarificationsUserSettings);
        hydrateWidgetSettings('priorAuth', setPriorAuthUserSettings);
        hydrateWidgetSettings('nonCovered', setNonCoveredUserSettings);
        hydrateWidgetSettings('theraInt', setTherapeuticIntUserSettings);
        hydrateWidgetSettings('admissionMeds', setAdmissionMedsUserSettings);
        hydrateWidgetSettings('refillTooSoon', setRefillTooSoonUserSettings);
        hydrateWidgetSettings('pumpPickup', setPumpPickupUserSettings);
        hydrateWidgetSettings('narcoticsRefill', setNarcoticsRefillUserSettings);
        hydrateWidgetSettings('alfNewScript', setAlfNewScriptUserSettings);
        hydrateWidgetSettings('alfCostResponse', setAlfCostResponseUserSettings);
        hydrateWidgetSettings('unavailableMedicationAlerts', setUnavailableMedicationAlertsUserSettings);
    }, [user?.userSettings]);

    // Wrap in useCallback to make it stable for dependency arrays
    const resetAlertStatesToDefault = React.useCallback(() => {
        resetHighCostAtom()
        resetHighCostAlertsAtom()
        resetClarificationsAtom()
        resetPriorAuthAtom()
        resetNonCoveredAtom()
        resetTherapeuticIntAtom()
        resetAdmissionMedsAtom()

        resetHighCostAtomFilteredCount()
        resetHighCostAlertsAtomFilteredCount()
        resetClarificationsAtomFilteredCount()
        resetPriorAuthAtomFilteredCount()
        resetNonCoveredAtomFilteredCount()
        resetTherapeuticIntAtomFilteredCount()
        resetAdmissionMedsAtomFilteredCount()

        resetHighCostAtomFiltered()
        resetHighCostAlertsAtomFiltered()
        resetClarificationsAtomFiltered()
        resetPriorAuthAtomFiltered()
        resetNonCoveredAtomFiltered()
        resetTherapeuticIntAtomFiltered()
        resetAdmissionMedsAtomFiltered()

        resetHighCostAtomShownAlerts()
        resetHighCostAlertsAtomShownAlerts()
        resetClarificationsAtomShownAlerts()
        resetPriorAuthAtomShownAlerts()
        resetNonCoveredAtomShownAlerts()
        resetTherapeuticIntAtomShownAlerts()
        resetAdmissionMedsAtomShownAlerts()

        resetHighCostAtomUserSettings()
        resetHighCostAlertsAtomUserSettings()
        resetClarificationsAtomUserSettings()
        resetPriorAuthAtomUserSettings()
        resetNonCoveredAtomUserSettings()
        resetTherapeuticIntAtomUserSettings()
        resetAdmissionMedsAtomUserSettings()
    }, [
        resetHighCostAtom, resetHighCostAlertsAtom, resetClarificationsAtom, resetPriorAuthAtom,
        resetNonCoveredAtom, resetTherapeuticIntAtom, resetAdmissionMedsAtom,
        resetHighCostAtomFilteredCount, resetHighCostAlertsAtomFilteredCount, resetClarificationsAtomFilteredCount,
        resetPriorAuthAtomFilteredCount, resetNonCoveredAtomFilteredCount, resetTherapeuticIntAtomFilteredCount,
        resetAdmissionMedsAtomFilteredCount, 
        resetHighCostAtomFiltered, resetHighCostAlertsAtomFiltered, resetClarificationsAtomFiltered,
        resetPriorAuthAtomFiltered, resetNonCoveredAtomFiltered, resetTherapeuticIntAtomFiltered,
        resetAdmissionMedsAtomFiltered, 
        resetHighCostAtomShownAlerts, resetHighCostAlertsAtomShownAlerts, resetClarificationsAtomShownAlerts,
        resetPriorAuthAtomShownAlerts, resetNonCoveredAtomShownAlerts, resetTherapeuticIntAtomShownAlerts,
        resetAdmissionMedsAtomShownAlerts, 
        resetHighCostAtomUserSettings, resetHighCostAlertsAtomUserSettings, resetClarificationsAtomUserSettings,
        resetPriorAuthAtomUserSettings, resetNonCoveredAtomUserSettings, resetTherapeuticIntAtomUserSettings,
        resetAdmissionMedsAtomUserSettings
    ]);
    
    const getUserInfo = React.useCallback(async () => {
        let localStorageFaciToken = localStorage.getItem('FACI_TOKEN');

        if (localStorageFaciToken !== undefined && localStorageFaciToken !== null) {
            try {
                const res = await axios.get(process.env.REACT_APP_API_BASE_URL + '/tokeninfo', {
                    headers: {
                        'Authorization': 'bearer ' + localStorageFaciToken
                    },
                    signal: abortControllerRef.current?.signal
                });
                
                SrxApi.defaults.headers.common['Authorization'] = 'bearer ' + localStorageFaciToken;
                SrxFsApi.defaults.headers.common['Authorization'] = 'bearer ' + localStorageFaciToken;
                setFaciToken(localStorageFaciToken);
                setUser({ ...res.data.userInfo, entitiesAndRoles: res.data.entitiesAndRoles || [] });
                setRoles(res.data.roles || []);
                setUserLoading(false);

            } catch (err) {
                if (axios.isCancel(err)) {
                    // Request was cancelled, exit early
                    return;
                }

                // FULL CLEANUP IF TOKEN IS INVALID
                CleanupService.performFullCleanupSync(resetAlertStatesToDefault);
                setFaciToken(null);
                setUser(null);
                setRoles([]);
                setUserLoading(false);
            }
        } else {
            // CLEANUP IF NO TOKEN
            setUserLoading(false);
            setFaciToken(null);
            setRoles([]);
            CleanupService.performFullCleanupSync(resetAlertStatesToDefault);
        }
    }, [resetAlertStatesToDefault]);

    // useMemo(() => {
    //     let userToken = localStorage.getItem('FACI_TOKEN');
    // }, [])

    useEffect(() => {
        abortControllerRef.current = new AbortController();

        // Centralized 401 interceptor: when any SrxApi call gets a 401 (expired token),
        // clean up auth state and redirect to login.
        const interceptorId = SrxApi.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error?.response?.status === 401) {
                    CleanupService.performFullCleanupSync(resetAlertStatesToDefault);
                    localStorage.removeItem('FACI_TOKEN');
                    SrxApi.defaults.headers.common['Authorization'] = undefined;
                    SrxFsApi.defaults.headers.common['Authorization'] = undefined;
                    setFaciToken(null);
                    setUser(null);
                    history.push('/account/login');
                }
                return Promise.reject(error);
            }
        );

        const initializeAuth = async () => {
            if (location.search) {
                let urlParams = new URLSearchParams(location.search);
                let token = urlParams.get('token');

                if (token) {
                    try {
                        const res = await axios.post(
                            process.env.REACT_APP_API_BASE_URL + '/loginWithToken',
                            null,
                            {
                                headers: { Authorization: `Bearer ${token}` },
                                signal: abortControllerRef.current.signal
                            }
                        );

                        clearVisionSession();
                        token = res.data.token;
                        SrxApi.defaults.headers.common['Authorization'] = 'bearer ' + token;
                        SrxFsApi.defaults.headers.common['Authorization'] = 'bearer ' + token;
                        localStorage.setItem('FACI_TOKEN', token);
                        await getUserInfo();
                    } catch (err) {
                        if (axios.isCancel(err)) return;
                        
                        SrxApi.defaults.headers.common['Authorization'] = undefined;
                        SrxFsApi.defaults.headers.common['Authorization'] = undefined;
                        setFaciToken(null);
                        localStorage.removeItem('FACI_TOKEN');
                        setUserLoading(false);
                    }
                } else {
                    await getUserInfo();
                }
            } else {
                await getUserInfo();
            }
        };
        
        initializeAuth();

        return () => {
            // Remove the 401 interceptor
            SrxApi.interceptors.response.eject(interceptorId);
            // Cancel any ongoing requests
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [])

    const loginUser = async (loginInfo) => {
        setUserLoading(true);

        // FULL CLEANUP BEFORE LOGIN - Ensures fresh state
        CleanupService.performFullCleanupSync(resetAlertStatesToDefault);
        
        // Create new abort controller for this login request
        const abortController = new AbortController();

        try {
            const tokenRequest = await axios.post(
                process.env.REACT_APP_API_BASE_URL + '/login',
                loginInfo,
                { signal: abortController.signal }
            );

            clearVisionSession();

            SrxApi.defaults.headers.common['Authorization'] = 'bearer ' + tokenRequest.data.token;
            SrxFsApi.defaults.headers.common['Authorization'] = 'bearer ' + tokenRequest.data.token;
            localStorage.setItem('FACI_TOKEN', tokenRequest.data.token);
            toast.success('User Logged In');
            setFaciToken(tokenRequest.data.token);
            setUser(tokenRequest.data.userInfo);
            setRoles(tokenRequest.data.roles || []);
            setUserLoading(false);

        } catch (err) {
            if (axios.isCancel(err)) {
                return;
            }
            const responseData = err?.response?.data;
            let parsedResponseData = responseData;
            if (typeof responseData === 'string') {
                try {
                    parsedResponseData = JSON.parse(responseData);
                } catch (parseError) {
                    parsedResponseData = responseData;
                }
            }
            const errorMessage =
                parsedResponseData?.message ||
                parsedResponseData?.error ||
                parsedResponseData?.title ||
                (Array.isArray(parsedResponseData?.errors) ? parsedResponseData.errors.join(', ') : null) ||
                (typeof parsedResponseData === 'string' ? parsedResponseData : null) 

            toast.error(errorMessage);
            setFaciToken(null);
            setUser(null);
            setRoles([]);
            setUserLoading(false);
        }
    }

    const logoutUser = () => {
        postNavigationActivity({
            user,
            pathname: location.pathname,
            actionType: 'logout',
            actionDetails: '',
        });

        // FULL CLEANUP ON LOGOUT
        CleanupService.performFullCleanupSync(resetAlertStatesToDefault);
        
        setFaciToken(null);
        setUser(null);
        setRoles([]);
        toast.success('User Logged Out');
        history.push('/account/login');
    }

    const clearVisionSession = () => {
        const visionIframe = document.querySelector('iframe[name="vision-frame"]') || 
                                document.querySelector('iframe#vision-iframe');
        
        if (visionIframe && visionIframe.contentWindow) {
            try {
                visionIframe.contentWindow.postMessage("CLEAR_SESSION", process.env.REACT_APP_VISION_HOST);
            } catch (error) {
                console.error('Failed to post message to Vision iframe:', error);
                localStorage.setItem('clear_vision_session', 'true');
            }
        } else {
            console.log('Vision iframe not found, setting flag');
            localStorage.setItem('clear_vision_session', 'true');
        }
    };

    // const logoutUser = () => {
    //     localStorage.removeItem('OMNI_TOKEN');
    //     OmniApi.defaults.headers.common['token'] = undefined;
    //     XOmniApi.defaults.headers.common['token'] = undefined;
    //     toast.success("User logged out", {
    //         theme: 'colored',
    //         position: "top-center",
    //         autoClose: 5000,
    //         hideProgressBar: true,
    //         closeOnClick: false,
    //         pauseOnHover: true,
    //         draggable: true,
    //         progress: undefined,
    //     });
    //     setUser('');
    //     history.push('/account/login');
    //     // !!!!!!!!!!!!!!!!!!! IMPORTANT: REMOVE TOKEN FROM AXIOS DEFAULTS. OMNI API !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    // } 



    return (
        <UserContext.Provider value={{ user, setUser, userLoading, loginUser, logoutUser, faciToken, setFaciToken, roles }}>
            {props.children}
        </UserContext.Provider>
    );
}

